import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  success: (msg: string) => void;
  error: (msg: string) => void;
  info: (msg: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = ++counterRef.current;
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const value: ToastContextValue = {
    success: (msg) => addToast('success', msg),
    error:   (msg) => addToast('error', msg),
    info:    (msg) => addToast('info', msg),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        style={s.container}
        aria-live="polite"
        aria-atomic="false"
        aria-label="Notifications"
      >
        {toasts.map(t => (
          <div
            key={t.id}
            style={{ ...s.toast, ...s[t.type] }}
            role="status"
          >
            <span style={s.icon}>{t.type === 'success' ? '✓' : t.type === 'error' ? '✕' : 'ℹ'}</span>
            <span style={s.msg}>{t.message}</span>
            <button
              style={s.closeBtn}
              onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
              aria-label="Dismiss notification"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed', bottom: '20px', right: '20px',
    display: 'flex', flexDirection: 'column', gap: '8px',
    zIndex: 9999, maxWidth: '360px', width: 'calc(100vw - 40px)',
  },
  toast: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 14px', borderRadius: '10px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    fontSize: '14px', fontWeight: 500, lineHeight: 1.4,
    animation: 'toastIn 0.2s ease',
  },
  success: { background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#14532D' },
  error:   { background: '#FEF2F2', border: '1px solid #FECACA', color: '#7F1D1D' },
  info:    { background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E3A5F' },
  icon: { fontSize: '15px', fontWeight: 700, flexShrink: 0 },
  msg:  { flex: 1 },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '18px', lineHeight: 1, padding: '0 0 0 4px',
    color: 'inherit', opacity: 0.6, flexShrink: 0,
  },
};
